import { useEffect, useState } from "react";
import { API_BASE_URL } from "../../config.tsx";
import { getFirstNameErrors, getLastNameErrors } from "../helpers/helpers.tsx";
import apiFetch, {
  getHeadsets, // Updated from getGroups
  getHeadquarters,
  getHospitalsInHeadquarter,
  getCountries,
} from "../helpers/requests.tsx";
import { User, UserRole } from "../helpers/interfaces.tsx";
import { useTranslation } from "react-i18next";

type ChangeUserDataModal = {
  setShowChangeDataModal: React.Dispatch<React.SetStateAction<boolean>>;
  user: User;
  handleChangeData: (updatedUser: User) => void;
  adminView?: boolean;
  headquarterView?: boolean;
  groupView?: boolean;
};

const ChangeUserDataModal: React.FC<ChangeUserDataModal> = ({
  setShowChangeDataModal,
  user,
  handleChangeData,
  adminView = false,
  headquarterView = false,
  groupView = false,
}) => {
  const [newFirstName, setNewFirstName] = useState(user.firstName || "");
  const [newLastName, setNewLastName] = useState(user.lastName || "");
  const [newHeadsetId, setNewHeadsetId] = useState(user.headsetId || "");
  const [headsetName, setHeadsetName] = useState(user.headsetName || "");
  const [headsetCountryCode, setHeadsetCountryCode] = useState(user.headsetCountryCode || "");
  const [newHeadquarterId, setNewHeadquarterId] = useState(user.headquarterId || "");
  const [headquarterName, setHeadquarterName] = useState(user.headquarterName || "");
  const [newHospitalId, setNewHospitalId] = useState(user.hospitalId || ""); // New state for hospital ID
  const [hospitalName, setHospitalName] = useState(user.hospitalName || "");     // New state for hospital name
  const [newRole, setNewRole] = useState<UserRole>(user.role || UserRole.Guest);

  const [groups, setGroups] = useState<any[]>([]);
  const [headquarters, setHeadquarters] = useState<any[]>([]);
  const [hospitals, setHospitals] = useState<any[]>([]); // New state for hospitals
  const [countries, setCountries] = useState<any[]>([]);

  const [saveDataSubmitted, setSaveDataSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(true); // New loading state

  const { t, i18n } = useTranslation();

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true); // Start loading

      try {
        let groupsList = [];
        if (adminView) {
          groupsList = await getHeadsets();
          setGroups(groupsList);

          const headquarterList = await getHeadquarters();
          setHeadquarters(headquarterList);
        }

        const countryList = await getCountries();
        countryList.sort((a: any, b: any) =>
          t("country_" + a.code).localeCompare(t("country_" + b.code))
        );
        setCountries(countryList);

        // Pre-fetch hospitals to ensure they are available when loading finishes
        const targetHeadquarterId = headquarterView ? user.headquarterId : user.headquarterId;
        const initialRole = user.role || UserRole.Guest;
        const shouldFetchHospitals = (adminView || headquarterView) && 
            (initialRole === UserRole.HeadquarterAdmin || initialRole === UserRole.User || initialRole === UserRole.HeadsetAdmin);

        if (shouldFetchHospitals && targetHeadquarterId) {
            const hospitalList = await getHospitalsInHeadquarter(targetHeadquarterId);
            setHospitals(hospitalList);
        }
      } catch (error) {
        console.error("Error fetching initial data:", error);
      } finally {
        setIsLoading(false); // End loading
      }
    };

    fetchData();
  }, []);

  // Effect to fetch hospitals when newHeadquarterId changes
  useEffect(() => {
    const fetchHospitalsForHeadquarter = async () => {
      const targetHeadquarterId = headquarterView ? user.headquarterId : newHeadquarterId;
      
      if (targetHeadquarterId) {
        try {
          const hospitalList = await getHospitalsInHeadquarter(targetHeadquarterId);
          setHospitals(hospitalList); // Assuming API returns array of hospitals
        } catch (error) {
          console.error("Error fetching hospitals:", error);
          setHospitals([]);
        }
      } else {
        setHospitals([]);
      }
    };

    // Only fetch hospitals if in admin view OR headquarter view and the role is one that would be associated with a headquarter/hospital
    const shouldFetch = (adminView || headquarterView) && (newRole === UserRole.HeadquarterAdmin || newRole === UserRole.User || newRole === UserRole.HeadsetAdmin);

    if (shouldFetch) {
      fetchHospitalsForHeadquarter();
    } else {
      setHospitals([]); // Clear hospitals if conditions are not met
      if (adminView) {
          setNewHospitalId(""); // Clear selected hospital
          setHospitalName(""); // Clear hospital name
      }
    }
  }, [newHeadquarterId, adminView, headquarterView, newRole, user.headquarterId]);

  const updateUserData = async (
    userId: string,
    newFirstName: string,
    newLastName: string,
    newHeadsetId: string,
    newHeadquarterId: string,
    newRole: UserRole,
    newHospitalId: string // Added newHospitalId
  ) => {
    try {
      await apiFetch(`/updateUserData`, {
        method: "POST",
        body: JSON.stringify({
          userId,
          newFirstName,
          newLastName,
          newHeadsetId,
          newHeadquarterId,
          newRole,
          newHospitalId, // Added newHospitalId
        }),
      });
    } catch (error) {
      console.error("Error update user data:", error);
      throw new Error("An error occurred while update user data");
    }
  };

  const handleRoleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const role = e.target.value as UserRole;
    setNewRole(role);

    if (role === UserRole.User || role === UserRole.HeadsetAdmin) {
      setNewHeadsetId("");
      setHeadsetName("");
    } else if (role === UserRole.HeadquarterAdmin) {
      setNewHeadsetId("");
      setHeadsetName("");
      setNewHospitalId(""); // Clear Hospital
      setHospitalName("");
      setHeadsetCountryCode("");
    } else if (role === UserRole.Guest) {
      setNewHeadsetId("");
      setHeadsetName("");
      setNewHeadquarterId("");
      setHeadquarterName("");
      setNewHospitalId(""); // Reset hospitalId
      setHospitalName(""); // Reset hospitalName
      setHeadsetCountryCode("");
    }

    setSaveDataSubmitted(false);
  };

  const handleSaveDataClick = async () => {
    setSaveDataSubmitted(true);

    if (
      getFirstNameErrors(newFirstName, t) ||
      getLastNameErrors(newLastName, t)
    ) {
      return;
    }

    if (newRole === UserRole.User || newRole === UserRole.HeadsetAdmin || newRole === UserRole.HeadquarterAdmin) {
      if (newHeadquarterId === "") return;
    }

    setShowChangeDataModal(false);

    const updatedUser: User = {
      id: user.id,
      firstName: newFirstName,
      lastName: newLastName,
      email: user.email,
      headsetId: "",
      headsetName: "",
      headsetCountryCode: headsetCountryCode,
      headquarterId: newHeadquarterId,
      headquarterName: headquarterName,
      hospitalId: newHospitalId, // Added hospitalId
      hospitalName: hospitalName, // Added hospitalName
      role: newRole,
      isStreaming: user.isStreaming,
    };

    handleChangeData(updatedUser);

    await updateUserData(
      user.id,
      newFirstName,
      newLastName,
      "", // HeadsetId is not used anymore for users
      newHeadquarterId,
      newRole,
      newHospitalId // Pass newHospitalId
    );
  };

  return (
    <div className="confirmation-modal">
      <div className="modal-content">
        {isLoading ? (
          <div style={{ textAlign: "center", padding: "20px" }}>
            {t("Loading...")}
          </div>
        ) : (
          <>
            <div style={{ textAlign: "center", fontSize: "1.3em" }}>
              {t("Edit_User")}
            </div>
            <div className="mb-3" style={{ marginTop: "2vh" }}>
              <label
                htmlFor="firstNameInput"
                style={{
                  marginLeft: "20px",
                  color: "#404040",
                  fontWeight: "bold",
                }}
              >
                {t("First_Name")}
              </label>
              <input
                type="text"
                id="firstNameInput"
                className="form-control"
                value={newFirstName}
                onChange={(e) => {
                  setNewFirstName(e.target.value);
                  setSaveDataSubmitted(false);
                }}
                style={{
                  backgroundColor: "#CCCCCC",
                  borderRadius: "20px",
                  color: "#404040",
                  height: "5vh",
                  maxHeight: "40px",
                  fontSize: "1em",
                  paddingLeft: "20px",
                }}
              />
              <label
                htmlFor="firstNameInput"
                style={{
                  marginLeft: "20px",
                  color: "#A63939",
                  fontSize: "0.8em",
                  visibility:
                    saveDataSubmitted && getFirstNameErrors(newFirstName, t)
                      ? "visible"
                      : "hidden",
                }}
              >
                {getFirstNameErrors(newFirstName, t)}
              </label>
            </div>
            <div className="mb-3" style={{ marginTop: "2vh" }}>
              <label
                htmlFor="lastNameInput"
                style={{
                  marginLeft: "20px",
                  color: "#404040",
                  fontWeight: "bold",
                }}
              >
                {t("Last_Name")}
              </label>
              <input
                type="text"
                id="lastNameInput"
                className="form-control"
                value={newLastName}
                onChange={(e) => {
                  setNewLastName(e.target.value);
                  setSaveDataSubmitted(false);
                }}
                style={{
                  backgroundColor: "#CCCCCC",
                  borderRadius: "20px",
                  color: "#404040",
                  height: "5vh",
                  maxHeight: "40px",
                  fontSize: "1em",
                  paddingLeft: "20px",
                }}
              />
              <label
                htmlFor="lastNameInput"
                style={{
                  marginLeft: "20px",
                  color: "#A63939",
                  fontSize: "0.8em",
                  visibility:
                    saveDataSubmitted && getLastNameErrors(newLastName, t)
                      ? "visible"
                      : "hidden",
                }}
              >
                {getLastNameErrors(newLastName, t)}
              </label>
            </div>
            {(adminView || headquarterView || groupView) && (
              <>
                <div className="mb-3" style={{ marginTop: "2vh" }}>
                  <label
                    htmlFor="roleSelect"
                    style={{
                      marginLeft: "20px",
                      color: "#404040",
                      fontWeight: "bold",
                    }}
                  >
                    {t("Role")}
                  </label>
                  <select
                    id="roleSelect"
                    className="form-control"
                    value={newRole}
                    style={{
                      backgroundColor: "#CCCCCC",
                      borderRadius: "20px",
                      color: "#404040",
                      height: "5vh",
                      maxHeight: "40px",
                      fontSize: "1em",
                      paddingLeft: "20px",
                    }}
                    onChange={handleRoleChange}
                  >
                    {adminView && <option value="guest">{t("Guest")}</option>}
                    {(adminView || headquarterView || groupView) && (
                      <>
                        <option value="user">{t("User")}</option>{" "}
                        {/* Updated key */}
                        <option value="headsetAdmin">
                          {t("Headset_Manager")}{" "}
                          {/* Updated key */}
                        </option>
                      </>
                    )}
                    {(adminView || headquarterView) && (
                      <option value="headquarterAdmin">
                        {t("Headquarter_Administrator")}
                      </option>
                    )}
                  </select>
                </div>
              </>
            )}

            {(newRole === UserRole.HeadquarterAdmin ||
              newRole === UserRole.User ||
              newRole === UserRole.HeadsetAdmin) &&
              adminView && (
                <>
                  <div className="mb-3" style={{ marginTop: "2vh" }}>
                    <label
                      htmlFor="headquarterSelect"
                      style={{
                        marginLeft: "20px",
                        color: "#404040",
                        fontWeight: "bold",
                      }}
                    >
                      {t("Headquarter")}
                    </label>
                    <select
                      id="headquarterSelect"
                      className="form-control"
                      value={newHeadquarterId}
                      style={{
                        backgroundColor: "#CCCCCC",
                        borderRadius: "20px",
                        color: "#404040",
                        height: "5vh",
                        maxHeight: "40px",
                        fontSize: "1em",
                        paddingLeft: "20px",
                      }}
                      onChange={(e) => {
                        const selectedHeadquarterId = e.target.value;
                        const selectedHeadquarter = headquarters.find(
                          (headquarter) =>
                            headquarter.id === selectedHeadquarterId
                        );
                        if (selectedHeadquarter) {
                          setNewHeadquarterId(selectedHeadquarterId);
                          setHeadquarterName(selectedHeadquarter.name);
                          setSaveDataSubmitted(false);
                        }
                      }}
                    >
                      <option value=""></option>
                      {headquarters.map((headquarter: any) => (
                        <option key={headquarter.id} value={headquarter.id}>
                          {headquarter.name}
                        </option>
                      ))}
                    </select>
                    <label
                      htmlFor="headquarterSelect"
                      style={{
                        marginLeft: "20px",
                        color: "#A63939",
                        fontSize: "0.8em",
                        visibility:
                          saveDataSubmitted && newHeadquarterId === ""
                            ? "visible"
                            : "hidden",
                      }}
                    >
                      {t("Headquarter_is_required")}
                    </label>
                  </div>
                </>
              )}

            {/* Hospital Select */}
            {(newRole === UserRole.User || newRole === UserRole.HeadsetAdmin) &&
              ((adminView && newHeadquarterId) || headquarterView) && (
                <div className="mb-3" style={{ marginTop: "2vh" }}>
                  <label
                    htmlFor="hospitalSelect"
                    style={{
                      marginLeft: "20px",
                      color: "#404040",
                      fontWeight: "bold",
                    }}
                  >
                    {t("Hospital")}
                  </label>
                  <select
                    id="hospitalSelect"
                    className="form-control"
                    value={newHospitalId}
                    style={{
                      backgroundColor: "#CCCCCC",
                      borderRadius: "20px",
                      color: "#404040",
                      height: "5vh",
                      maxHeight: "40px",
                      fontSize: "1em",
                      paddingLeft: "20px",
                    }}
                    onChange={(e) => {
                      const selectedHospitalId = e.target.value;
                      const selectedHospital = hospitals.find(
                        (hospital) => hospital.id === selectedHospitalId
                      );
                      if (selectedHospital) {
                        setNewHospitalId(selectedHospitalId);
                        setHospitalName(selectedHospital.name);
                        setHeadsetCountryCode(selectedHospital.countryCode);
                        setSaveDataSubmitted(false);
                      } else {
                        setNewHospitalId("");
                        setHospitalName("");
                        setHeadsetCountryCode("");
                      }
                    }}
                  >
                    <option value=""></option>
                    {hospitals.map((hospital: any) => (
                      <option key={hospital.id} value={hospital.id}>
                        {hospital.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

            <div className="button-container">
              <button
                type="button"
                className="buttonCancel btn btn-primary"
                onClick={() => {
                  setShowChangeDataModal(false);
                  setSaveDataSubmitted(false);
                }}
              >
                {t("Cancel")}
              </button>
              <button
                type="button"
                className="buttonAcceptGreen btn btn-primary"
                onClick={() => handleSaveDataClick()}
              >
                {t("Save")}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ChangeUserDataModal;